<template>
  <div>
    <!-- Metric Section Header (exclude if no metrics are in search results) -->
    <div v-if="showInSearch" class="flex items-center gap-1 mb-2.5">
      <component :is="sectionIcon" class="text-icon-muted" />
      <BaseText :id="sectionId" :ref="sectionId" type="heading" size="sm" class="capitalize text-text-muted metric-section">
        {{ sectionId }}
      </BaseText>
    </div>
    <!-- Base metric list (render before any child groups) -->
    <div v-for="metric in searchList.metrics" :id="`metric-${metric.key}`" :key="metric.key"
    class="flex items-center gap-3 relative p-1.5 hover:bg-background-normal cursor-pointer rounded-lg"
    @click="handleMetricAdd(metric.key, sectionId)" @mouseenter="addHover(metric)" @mouseleave="addHover(null)">
      <input type="checkbox" :checked="presetList.includes(metric.key)"
      class="h-4 w-4 rounded checkbox-input text-black cursor-pointer focus:ring-transparent transition-colors">
      <BaseText type="label" size="sm" class="text-neutral-600">
        {{ metric.name }}
      </BaseText>
    </div>
    <!-- Sub sections / child groups list -->
    <div v-for="(subSection, index) in searchList.subSections" :key="index">
      <div>
        <BaseText :id="subSection.domId" :ref="subSection.domId"
        type="heading" size="overline" class="text-text-subdued mt-4 mb-1 uppercase metric-section">
          {{ subSection.sectionId }}
        </BaseText>
        <!-- List of all metrics in the child group -->
        <div class="flex flex-col gap-1">
          <div v-for="metric in subSection?.metrics" :id="`metric-${metric.key}`" :key="metric.key"
          class="flex items-center gap-2 relative p-1.5 hover:bg-background-normal cursor-pointer rounded-lg"
          @click="handleMetricAdd(metric.key, sectionId)" @mouseenter="addHover(metric)" @mouseleave="addHover(null)">
            <input type="checkbox" :checked="presetList.includes(metric.key)"
            class="h-4 w-4 rounded checkbox-input text-black cursor-pointer focus:ring-transparent transition-colors">
            <BaseText type="label" size="sm" class="capitalize text-neutral-600">
              {{ metric.name }}
            </BaseText>
          </div>
        </div>
      </div>
    </div>
    <!-- Table section list -->
    <div v-for="tableSection in searchList.tableSections" :key="tableSection.sectionId">
      <div>
        <!-- Table section HEADER -->
        <div class="flex items-center mt-4 mb-1 bg-background-disabled rounded-lg pl-2.5 py-2.5 pr-3.5 h-9 min-w-0">
          <BaseText :id="tableSection.domId" :ref="tableSection.domId"
          type="heading" size="overline" class="flex-grow text-text-subdued uppercase metric-section">
            {{ tableSection.sectionId.replace(/_/g, ' ') }}
          </BaseText>
          <div class="grid place-items-center gap-x-8"
          :style="{ gridTemplateColumns: `repeat(${tableSection.colHeaders.length}, 40px)` }">
            <BaseText v-for="tableHeader in tableSection.colHeaders" :key="tableHeader"
            type="heading" size="overline" class="relative left-1 text-text-subdued uppercase">
              {{ tableHeader }}
            </BaseText>
          </div>
        </div>
        <!-- Table section ROWS -->
        <div v-for="row in tableSection.metrics" :key="row.rowName"
        class="flex items-center relative pl-1.5 py-1.5 pr-2.5 rounded-lg min-w-0">
          <div class="flex-grow flex items-center gap-2.5 min-w-0">
            <BaseText type="label" size="sm" class="flex-shrink capitalize text-neutral-600 truncate">
              {{ row.rowName }}
            </BaseText>
            <!-- For custom conversions, render their id -->
            <div v-if="tableSection.sectionId === 'custom_conversions' && row.metrics?.length" 
            class="px-1.5 py-0.5 rounded-full bg-neutral-25 whitespace-nowrap">
              <BaseText type="label" size="xs" class="text-text-subdued">
                {{ row.metrics[0].note }}
              </BaseText>
            </div>
          </div>
          <div class="grid place-items-center gap-x-8"
          :style="{ gridTemplateColumns: `repeat(${row.metrics.length}, 40px)` }">
            <div v-for="(tableMetric) in row.metrics" :id="`metric-${tableMetric.key}`" :key="tableMetric.key"
            class="h-4 w-4 flex items-center justify-center">
              <div v-if="tableMetric.is_null" class="h-4 w-4 checkbox-input rounded bg-neutral-50 opacity-50" />
              <input v-else
                type="checkbox" 
                class="rounded checkbox-input text-black cursor-pointer focus:ring-transparent transition-colors"
                :checked="presetList.includes(tableMetric.key)"
                @click="handleMetricAdd(tableMetric.key, sectionId)"
                @mouseenter="addHover(tableMetric)"
                @mouseleave="addHover(null)"
              >
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="sectionId === 'conversions' && getMetricsPlatform === 'meta' && showInSearch" :class="hasCustomConversions ? 'mt-3' : 'mt-6'"
    class="flex items-start gap-2 pl-2.5 pr-4 py-2.5 rounded-md bg-neutral-25">
      <InfoRectIcon class="text-icon-normal flex-shrink-0" />
      <div class="flex-grow flex flex-col gap-3">
        <BaseText type="body" size="sm" class="text-text-normal">
          Custom conversions are automatically loaded based on your setup in Meta ads manager. When you create new custom conversions they will appear in this section.
        </BaseText>
        <a href="https://foreplay.featurebase.app/en/help/articles/7428583-custom-conversions" target="_blank" class="w-max">
          <BaseText type="label" size="sm" class="text-text-normal transition-colors duration-100 hover:text-text-muted">
            Learn More
          </BaseText>
        </a>
      </div>
    </div>
    <div v-if="showInSearch && showDivider" class="w-full border-t border-border-normal my-8" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'

import InfoRectIcon from '../../globals/Icons/InfoRectIcon.vue'

export default {
  name: 'MetricSectionList',
  components: {
    InfoRectIcon
  },
  props: {
    presetList: {
      type: Array,
      default: () => []
    },
    data: {
      type: Object,
      default: () => {}
    },
    sectionId: {
      type: String,
      default: () => ''
    },
    sectionIcon: {
      type: Object,
      default: () => null
    },
    fuzzyResults: {
      type: Set,
      default: () => new Set()
    },
    // Suppressed when no other section follows this one, to avoid a trailing divider
    showDivider: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {

    }
  },
  computed: {
    ...mapGetters('MetricsModule', ['getMetricsPlatform', 'getMetricSearch']),
    showInSearch () {
      return (this.getMetricSearch.length === 0 || (this.searchList.metrics.length > 0 || this.searchList.subSections.length > 0 || this.searchList.tableSections.length > 0))
    },
    hasCustomConversions () {
      return this.searchList.tableSections.some(section => section.sectionId === 'custom_conversions')
    },
    searchList () {
      // Handle user search
      if (this.getMetricSearch.length > 0) {
        // Base metrics
        const searchMetrics = this.filterSearchResults(this.data.metrics)

        // Sub metrics
        const searchSubs = this.data.subSections.reduce((acc, section) => {
          const filteredMetrics = this.filterSearchResults(section.metrics)
          if (filteredMetrics.length > 0) {
            acc.push({ ...section, metrics: filteredMetrics })
          }
          return acc
        }, [])

        // Table metrics
        const searchTables = this.data.tableSections?.length > 0 ? this.data.tableSections.reduce((acc, section) => {
          const filteredMetrics = section.metrics.filter((row) => row.metrics.some((metric) => this.fuzzyResults.has(metric.key)))
          if (filteredMetrics.length > 0) {
            acc.push({ ...section, metrics: filteredMetrics })
          }
          return acc
        }, []) : []

        return {
          metrics: searchMetrics,
          subSections: searchSubs,
          tableSections: sortCustomConversionsToEnd(searchTables)
        }
      }
      // All metrics
      return {
        metrics: this.data.metrics,
        subSections: this.data.subSections,
        tableSections: sortCustomConversionsToEnd(this.data.tableSections)
      }
    }
  },
  methods: {
    filterSearchResults (arr) {
      return arr.filter((val) => this.fuzzyResults.has(val.key))
    },
    addHover (metricKey) {
      this.$emit('handleHover', metricKey)
    },
    handleMetricAdd (metricKey, parentId) {
      this.$emit('add', metricKey, parentId)
    }
  }
}

const sortCustomConversionsToEnd = (arr) => {
  if (!arr?.length) return arr
  return arr.sort((a, b) => {
    if (a.sectionId === 'custom_conversions') return 1
    if (b.sectionId === 'custom_conversions') return -1
    return 0
  })
}
</script>

<style scoped>
.checkbox-input {
  border: 1px;
  box-shadow: 0px 0px 0px 1px rgba(9, 25, 72, 0.13), 0px 1px 2px 0px rgba(9, 25, 72, 0.13);
}
</style>
